import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { MonthlyTrendPoint } from "@/services/types";
import { formatINRNumber } from "@/utils/format";
import { useThemeColors } from "@/hooks/useThemeColors";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatTrendMonth(value: string | undefined): string {
  if (!value) return "";
  const trimmed = String(value).trim();
  const ym = /^(\d{4})-(\d{1,2})/.exec(trimmed);
  if (ym) {
    const year = ym[1];
    const monthIdx = Number(ym[2]) - 1;
    if (monthIdx >= 0 && monthIdx < 12) {
      return `${MONTH_NAMES[monthIdx]} ${year.slice(2)}`;
    }
  }
  return trimmed;
}

export function TrendChart({ data }: { data: MonthlyTrendPoint[] }) {
  const theme = useThemeColors();
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={data} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
        <CartesianGrid stroke={theme.grid} vertical={false} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 11, fill: theme.text }}
          axisLine={false}
          tickLine={false}
          interval={0}
          angle={-30}
          textAnchor="end"
          height={44}
          tickFormatter={(v) => formatTrendMonth(String(v))}
        />
        <YAxis tick={{ fontSize: 11, fill: theme.text }} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => `₹${Math.round(Number(v) / 100000)}L`} />
        <Tooltip
          labelFormatter={(label) => formatTrendMonth(String(label))}
          formatter={(value, name) => [formatINRNumber(Number(value)), String(name).toLowerCase() === "collections" ? "Collections" : "Payouts"]}
          contentStyle={{ borderRadius: 8, border: "none", fontSize: 12, background: theme.tooltipBg, color: theme.tooltipText }}
          labelStyle={{ color: theme.tooltipText }}
          cursor={{ fill: theme.primary200 }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="collections" name="Collections" fill={theme.primary} radius={[3, 3, 0, 0]} maxBarSize={22} />
        <Line dataKey="payouts" name="Payouts" stroke={theme.accent} strokeWidth={2} dot={{ r: 2, fill: theme.accent }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}